// First step after verifying the email: every website belongs to an organisation. Same shell as sign-in
// (logo above the title, no top bar); «Cerrar sesión» sits under the form like the links on those screens.
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { Form } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { meQuery } from '../api';
import { authClient } from '../auth-client';
import { Button, Field, Message, Page } from '../ui';
import { readForm, slugify } from '../util';

export function NewOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const name = (readForm(event).name ?? '').trim();
    setBusy(true);
    setError(null);
    // The creator becomes its owner and the organisation becomes the session's active one.
    const { error: failure } = await authClient.organization.create({ name, slug: slugify(name) });
    setBusy(false);
    if (failure) {
      setError(t('errors.generic'));
      return;
    }
    await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    await navigate({ to: '/' });
  }

  async function signOut() {
    await authClient.signOut();
    queryClient.setQueryData(meQuery.queryKey, null);
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== meQuery.queryKey[0] });
    await navigate({ to: '/login' });
  }

  return (
    <Page title={t('organization.title')} brand>
      <p className="page-subtitle">{t('organization.intro')}</p>
      <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
        {error && <Message tone="error">{error}</Message>}
        <Field
          name="name"
          label={t('organization.name')}
          description={t('organization.nameHint')}
          autoComplete="organization"
          maxLength={80}
          isRequired
        />
        <Button type="submit" isDisabled={busy}>
          {t('organization.submit')}
        </Button>
      </Form>
      <p className="mt-6">
        <button type="button" className="link-button" onClick={() => void signOut()}>
          {t('app.signOut')}
        </button>
      </p>
    </Page>
  );
}
